import { ReactNode, createContext, useContext } from 'react';
import getConfig from 'next/config';
import { IterableEmbeddedProvider, useOnJwtRequest } from '@care/iterable-display';
import useFeatureFlag from '@/components/hooks/useFeatureFlag';
import { UI_FEATURE_FLAGS } from '@/constants';

const {
  publicRuntimeConfig: {
    ITERABLE_MFE_API_KEY,
    ITERABLE_MFE_PACKAGE_NAME,
    ITERABLE_MFE_GUEST_USER_ID,
    ITERABLE_TOP_BANNER_PLACEMENT_ID,
    HTL_ENV,
  },
} = getConfig();

const JOB_PLACEMENT_ID_MAP: Record<string, number> = {
  local: 1577,
  dev: 1577,
  stg: 1576,
  prod: 1575,
};

const IterablePlacementContext = createContext<number | undefined>(undefined);

export const useIterablePlacementId = () => {
  const context = useContext(IterablePlacementContext);
  if (context === undefined) {
    throw new Error('useIterablePlacementId must be used within <IterableWrapper>');
  }
  return context;
};

type IterableWrapperProps = {
  children: ReactNode;
  isJobSegment?: boolean;
  customPlacementId?: number;
};

export const IterableWrapper = ({
  children,
  isJobSegment = false,
  customPlacementId,
}: IterableWrapperProps) => {
  const iterableEmbeddedGuestMessagingEnabled = useFeatureFlag(
    UI_FEATURE_FLAGS.ITERABLE_EMBEDDED_GUEST_MESSAGING_ENABLED.id
  );

  const getJWTRequest = useOnJwtRequest(ITERABLE_MFE_GUEST_USER_ID);

  const resolvedPlacementId =
    customPlacementId ??
    (isJobSegment ? JOB_PLACEMENT_ID_MAP[HTL_ENV] : Number(ITERABLE_TOP_BANNER_PLACEMENT_ID));

  return (
    <IterableEmbeddedProvider
      userId={ITERABLE_MFE_GUEST_USER_ID}
      packageName={ITERABLE_MFE_PACKAGE_NAME}
      apiKey={ITERABLE_MFE_API_KEY}
      placementIds={[resolvedPlacementId]}
      onJwtRequest={getJWTRequest}
      disabled={!iterableEmbeddedGuestMessagingEnabled?.value}>
      <IterablePlacementContext.Provider value={resolvedPlacementId}>
        {children}
      </IterablePlacementContext.Provider>
    </IterableEmbeddedProvider>
  );
};
